/**
 * Things worth doing next, derived from the workspace's own data.
 *
 * ## WHY THIS EXISTS
 *
 * Wend's Overview answered *what happened, and why can you not see more of it*.
 * That is the fourth question a marketer asks. The first is *what should I do
 * next*, and nothing on the screen answered it.
 *
 * A calendar of dates worth campaigning on is the other half of that answer and
 * is **not built yet** — this paragraph said it shipped alongside this panel,
 * which was false, and a reader who went looking for it found nothing. What
 * exists today is the dataset: two years of Indian holidays, extracted and
 * checked in, read by nothing.
 *
 * The two halves are deliberately different in kind, and that is why this one
 * came first. A calendar can only ever be as good as what somebody loaded for a
 * given country — `date.nager.at` answers **HTTP 204, nothing at all, for India
 * and the UAE**, and the offline `date-holidays` package gives India six days
 * with not one festival among them. **This file needs nothing loaded.** Every
 * count below works on the day a workspace imports its first list, in any
 * country, in any line of business.
 *
 * ## EVERY OPPORTUNITY HERE IS DERIVED FROM DATA THIS PRODUCT ALREADY HOLDS
 *
 * That is the design constraint, and it is what makes this work for a resort, a
 * bank and an event company without any of them configuring anything. Contacts,
 * the messages sent to them, templates and segments exist for every tenant from
 * the first import. None of the four below asks what line of business anybody is
 * in, and none of them can be wrong about a country.
 *
 * ## AND NONE OF THEM MAY REST ON AN OUTCOME NOBODY REPORTED
 *
 * The obvious fifth opportunity is *"repeat the campaign that did best"*, and it
 * is deliberately absent. On a workspace with no delivery reports there is no
 * open rate, no click rate and no deliverability figure — so "did best" would be
 * a ranking over data that does not exist, which is the shape of both criticals
 * this project has shipped. When engagement is measured it can be added, gated
 * on the measurement the way every rate on the Overview already is. Until then
 * the four here rest on counts and dates, which are known for certain.
 *
 * ## SUPPRESSED PEOPLE ARE NEVER IN A COUNT
 *
 * Somebody who unsubscribed, hard-bounced or complained is not an opportunity,
 * and a product that offers to message them again has misunderstood what it is
 * for. Both audience reads exclude the suppression list, per channel, and the
 * API suite asserts it against a real suppressed row rather than trusting the
 * SQL to have said so.
 *
 * ## AND SINCE SLICE 50, THREE OF THE FIVE COUNTS CAN BECOME AN AUDIENCE
 *
 * Slice 32 shipped this panel with a known gap in its own record: *"Its
 * suggestions cannot become audiences. '1,284 people have not heard from you in
 * 90 days' is true, and the campaign form's segment picker cannot express it."*
 * The bottom half of this file closes it. `never_messaged`, `new_people` and
 * `dormant_audience` are populations of PEOPLE and can be saved as a **cohort
 * segment**; `unused_template` and `segment_never_sent` count templates and
 * segments and cannot. See {@link SAVEABLE_OPPORTUNITY_KIND_VALUES}.
 *
 * What makes the saved audience the same audience is that the membership is
 * written by an `INSERT ... SELECT` whose SELECT is the very SQL that produced
 * the count (`apps/api/src/modules/overview/opportunity-population.ts`) — one
 * text used twice, so there are not two queries that can disagree and the card
 * cannot promise 1,284 while the campaign reaches 900.
 *
 * Every word a saved cohort shows is defined here and nowhere else: its default
 * name, and every sentence said when a save is refused. None of them is an
 * explanatory sentence added to a screen.
 */
import { z } from 'zod';

/* -------------------------------------------------------------------------- */
/* The four                                                                    */
/* -------------------------------------------------------------------------- */

export const OPPORTUNITY_KIND_VALUES = [
  'never_messaged',
  'new_people',
  'dormant_audience',
  'unused_template',
  'segment_never_sent',
] as const;

export const opportunityKindSchema = z.enum(OPPORTUNITY_KIND_VALUES);
export type OpportunityKind = z.infer<typeof opportunityKindSchema>;

/**
 * How long without a message makes an audience dormant.
 *
 * Ninety days is a choice, not a law, and it is named here so that it is one
 * number rather than one per query. It is deliberately long: a fortnight of
 * quiet is a normal sending rhythm, and an Overview that called it a problem
 * would be nagging rather than helping — which is how a reader learns to ignore
 * the panel that matters.
 *
 * The screen prints it, so a reader knows what "dormant" was measured as rather
 * than having to guess at the product's opinion.
 */
export const OPPORTUNITY_DORMANT_DAYS = 90;

/* -------------------------------------------------------------------------- */
/* The shape                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * What the tile's one control DOES, and what it says.
 *
 * A **discriminated union since slice 50**, and which arm the API sends is the
 * only thing that decides whether a tile draws a link or a button. The screen
 * holds no list of saveable kinds: a second list would be a second opinion about
 * which counts are a population of people, and the one that matters is the one
 * the database enforces.
 *
 * `link` carries an `href`. `href` rather than a route name because the screen
 * already builds links that way everywhere else, and a second scheme for this one
 * panel is a second thing to keep in step.
 *
 * `save` carries **no `href`, and that absence is the point**. Where a save goes
 * is decided by what it wrote — the id of the segment that did not exist when the
 * card was rendered — so a destination on this arm could only be a guess made
 * before the thing it points at existed. The client follows the response.
 */
export const opportunityLinkActionSchema = z
  .object({
    kind: z.literal('link'),
    label: z.string().min(1),
    href: z.string().min(1),
  })
  .strict();

export const opportunitySaveActionSchema = z
  .object({
    kind: z.literal('save'),
    label: z.string().min(1),
  })
  .strict();

export const opportunityActionSchema = z.discriminatedUnion('kind', [
  opportunityLinkActionSchema,
  opportunitySaveActionSchema,
]);
export type OpportunityAction = z.infer<typeof opportunityActionSchema>;

/**
 * One thing worth doing, with the count that makes it worth doing.
 *
 * **`count` is `.min(1)`, and that is the whole guarantee of this panel.** An
 * opportunity over nothing is not an opportunity: *"0 people have never been
 * messaged"* is a sentence with no action in it, and a panel that prints four of
 * those every day is a panel nobody reads. The API drops an empty one rather
 * than sending a zero, and the shape refuses it if that ever regresses.
 */
export const opportunitySchema = z
  .object({
    kind: opportunityKindSchema,
    count: z.number().int().min(1),
    action: opportunityActionSchema,
  })
  .strict();
export type Opportunity = z.infer<typeof opportunitySchema>;

export const opportunitiesResponseSchema = z
  .object({
    generatedAt: z.string().min(1),
    /** Most worth doing first. Empty when there is genuinely nothing. */
    opportunities: z.array(opportunitySchema),
  })
  .strict();
export type OpportunitiesResponse = z.infer<typeof opportunitiesResponseSchema>;

/* -------------------------------------------------------------------------- */
/* The words                                                                   */
/* -------------------------------------------------------------------------- */

interface OpportunityCopy {
  /**
   * Two or three words naming the kind, for the chip above the headline.
   *
   * It says what the count is *about* rather than what to do about it, because
   * the button below already says that and a card whose chip and button both
   * read "Create campaign" has spent two lines saying one thing.
   */
  readonly label: string;
  /**
   * The dot beside the label in the approved design. It is a CATEGORY colour,
   * not a verdict: `warn` on a dormant audience says "this is the people-gone-
   * quiet one", not "this is bad". Nothing on this card is judged against a
   * threshold, which is why `bad` is never used here.
   */
  readonly tone: 'good' | 'warn' | 'info' | 'neutral';
  /**
   * The design's mono line above the button — where the figure came from.
   *
   * It names the data, never the advice. The mockup writes things like
   * "Segment · All subscribers"; this product does not know which segment a
   * count came from unless the read says so, and naming one it did not use
   * would be a claim about provenance, which is the one kind of claim a line
   * like this exists to make truthfully.
   */
  readonly source: string;
  /** The headline, built from the count so the screen cannot drift from it. */
  readonly headline: (count: number) => string;
}

/**
 * The one qualifier that used to be printed four times.
 *
 * Every tile carried a `detail` sentence, and measured against the headline
 * directly above it, three of the four said nothing new: *"Built, and no
 * campaign has been sent from them"* under *"3 templates have never been
 * used"*. The two audience sentences carried exactly one fact between them —
 * that suppressed people are left out — and printed it twice on one card.
 *
 * Fifty of the card's 105 words were those four sentences. They are one
 * sentence now, said once, under the grid, and it says **why** rather than
 * merely that: somebody who asked not to be contacted is not an opportunity.
 * The qualifier that could not be folded into a headline — a segment being
 * counted only when somebody is actually in it — went into the headline
 * instead.
 *
 * No honesty state left the screen. One of them stopped being said four times.
 */
export const OPPORTUNITY_SUPPRESSION_NOTE =
  'Nobody who unsubscribed, bounced or complained is counted here.';

/**
 * Total over {@link OPPORTUNITY_KIND_VALUES}, so a new kind is a compile error
 * here rather than a blank card.
 *
 * **Every sentence is a fact about this workspace, and none is a prediction.**
 * The first draft of `never_messaged` ended *"a first message inside a week does
 * measurably better than one a month later"*, which reads well and is a claim
 * about marketing that this product has measured nothing to support. It came
 * out. What is left says what is true — these people are on your lists and
 * nothing has gone to them — and leaves the judgement to the person whose
 * business it is.
 */
export const OPPORTUNITY_COPY: Readonly<Record<OpportunityKind, OpportunityCopy>> = {
  never_messaged: {
    label: 'Never messaged',
    tone: 'info',
    source: 'Contacts \u00b7 nothing sent to them',
    headline: (count) =>
      count === 1
        ? 'One person has never been messaged'
        : `${count.toLocaleString('en-IN')} people have never been messaged`,
  },
  new_people: {
    label: 'New people',
    tone: 'info',
    source: 'Contacts \u00b7 added since your last send',
    /*
     * "OF THOSE NEVER MESSAGED" IS THE TILE'S SAFETY PROPERTY, NOT A FLOURISH.
     *
     * This count is a strict SUBSET of `never_messaged`. A person all of whose
     * contact rows were created after `max(sent_at)` cannot hold a message,
     * because the provider would have had to accept it before its own
     * recipient row existed. Both counts come from one CTE in one statement, so
     * `new_people <= never_messaged` holds by construction rather than by two
     * queries agreeing — and since `count` is `.min(1)`, the never-messaged
     * tile is always on screen whenever this one is.
     *
     * The clause is what stops a skimmer adding two numerals that describe the
     * same humans. It is asserted rather than trusted; see
     * `verify-opportunities`.
     *
     * **"added", not "joined".** What Wend witnessed is a row being written by
     * an import, not a person joining anything — the mockup's "joined" is a
     * claim about the person's relationship to the business, which this product
     * did not observe. **"people", not "contacts"**: the unit is the person
     * (`person_id`, since slice 58), and "contacts" is the row word.
     *
     * The mockup's second line — *"None of them has had a welcome."* — is not
     * printed. "A welcome" is a kind of message this product has no concept of;
     * what is true is that nothing has gone to them, and the headline says that
     * by naming the population it is a subset of.
     */
    headline: (count) =>
      count === 1
        ? 'One of those never messaged was added since your last send'
        : `${count.toLocaleString('en-IN')} of those never messaged were added since your last send`,
  },
  dormant_audience: {
    label: 'Dormant audience',
    tone: 'warn',
    source: `Contacts \u00b7 quiet ${String(OPPORTUNITY_DORMANT_DAYS)} days`,
    headline: (count) =>
      count === 1
        ? `One person has not heard from you in ${String(OPPORTUNITY_DORMANT_DAYS)} days`
        : `${count.toLocaleString('en-IN')} people have not heard from you in ${String(OPPORTUNITY_DORMANT_DAYS)} days`,
  },
  unused_template: {
    label: 'Unused template',
    tone: 'neutral',
    source: 'Templates',
    /*
     * "never been used" means no campaign has SENT from it, which is what the
     * read was changed to ask. Its first version disqualified a template as
     * soon as any campaign row named it — including a draft nobody ever sent —
     * which would have hidden exactly the template somebody built, half-wired
     * into a draft, and forgot.
     */
    headline: (count) =>
      count === 1
        ? 'One template has never been used'
        : `${count.toLocaleString('en-IN')} templates have never been used`,
  },
  segment_never_sent: {
    label: 'Unused segment',
    tone: 'neutral',
    source: 'Segments',
    /*
     * "with people in them" is in the headline rather than in a sentence below
     * it because it is the qualifier that cannot be dropped: the read counts a
     * segment only when a contact is reachable through it, so a segment pointed
     * at an upload that is still importing is not on this list. Said here it
     * costs four words; said underneath it cost twelve and repeated the rest of
     * the headline to get to them.
     */
    headline: (count) =>
      count === 1
        ? 'One segment with people in it has never been sent to'
        : `${count.toLocaleString('en-IN')} segments with people in them have never been sent to`,
  },
};

/**
 * The order the panel draws them in, most actionable first.
 *
 * An audience with nobody reaching it is worth more than a template nobody used,
 * because the first is people and the second is housekeeping. Stated as data so
 * the API and the screen cannot disagree about which matters.
 */
export const OPPORTUNITY_ORDER: readonly OpportunityKind[] = [
  'dormant_audience',
  'never_messaged',
  /*
   * DIRECTLY AFTER ITS CONTAINER, AND THAT IS THE POINT.
   *
   * `new_people` is a strict subset of `never_messaged`, and its headline says
   * so — "N of those never messaged". A reader meets the container first, so
   * the referent of "those" is the tile immediately before it rather than a
   * position on a grid, which keeps the sentence true however the tiles wrap.
   */
  'new_people',
  'segment_never_sent',
  'unused_template',
];

/**
 * The only supported way to build one.
 *
 * Refuses a count below one rather than letting a zero reach the wire, so the
 * caller's `if (count > 0)` is a convenience rather than the thing standing
 * between a reader and *"0 people have never been messaged"*.
 */
export function opportunity(
  kind: OpportunityKind,
  count: number,
  action: OpportunityAction,
): Opportunity | null {
  if (!Number.isFinite(count) || count < 1) return null;
  return { kind, count: Math.floor(count), action };
}

/* -------------------------------------------------------------------------- */
/* Slice 50 — the three counts that are a population of people                 */
/* -------------------------------------------------------------------------- */

/**
 * The kinds that can be SAVED as an audience.
 *
 * Slice 32 recorded the gap this closes: *"Its suggestions cannot become
 * audiences. '1,284 people have not heard from you in 90 days' is true, and the
 * campaign form's segment picker cannot express it."* A saved one is a **cohort
 * segment** — `type = 'cohort'`, membership in `public.segment_contacts`, one
 * row for every `contacts` row of every person the card counted.
 *
 * Three of the five, and the missing two are not an oversight.
 * `unused_template` counts TEMPLATES and `segment_never_sent` counts SEGMENTS;
 * neither is a population of people, so neither can be a set of contacts. The
 * database says the same thing from the other side —
 * `segments_cohort_kind_is_an_audience` in `0037_segment_contacts.sql` admits
 * exactly these three labels — and {@link saveableOpportunityKindSchema}
 * refuses the other two at the edge with a 400 rather than letting a 23514
 * reach a screen.
 *
 * This is deliberately **not** a Postgres enum. It is a narrower list than
 * {@link OPPORTUNITY_KIND_VALUES} over the same vocabulary, and a database enum
 * holding a subset of another enum's labels reads as a second opinion about the
 * same words. `segments.cohort_kind` is `text` plus a CHECK, which also means
 * the day a fourth count becomes saveable is one CHECK and not the two-file
 * enum dance 0036 and 0037 pay for.
 */
export const SAVEABLE_OPPORTUNITY_KIND_VALUES = [
  'never_messaged',
  'new_people',
  'dormant_audience',
] as const;

export const saveableOpportunityKindSchema = z.enum(SAVEABLE_OPPORTUNITY_KIND_VALUES);
export type SaveableOpportunityKind = z.infer<typeof saveableOpportunityKindSchema>;

/**
 * Total over {@link OPPORTUNITY_KIND_VALUES}, so a sixth kind is a compile error
 * here rather than a tile whose button silently does nothing.
 *
 * `true` is not "this tile has a button" — every tile has one. It is "this
 * tile's button can save what it counted", which is a fact about the *unit* of
 * the count and nothing else.
 */
export const OPPORTUNITY_IS_SAVEABLE: Readonly<Record<OpportunityKind, boolean>> = {
  never_messaged: true,
  new_people: true,
  dormant_audience: true,
  unused_template: false,
  segment_never_sent: false,
};

/** Narrows a kind off the wire without repeating the three labels. */
export function isSaveableOpportunityKind(kind: OpportunityKind): kind is SaveableOpportunityKind {
  return OPPORTUNITY_IS_SAVEABLE[kind];
}

/**
 * What the two unsaveable kinds are counts OF, for the refusal sentence.
 *
 * Kept as data rather than written into the sentence so the refusal can name
 * which of the two it was — *"a count of templates"* and *"a count of
 * segments"* call for the same repair but are not the same mistake, and a
 * refusal that says "not of people" without saying what it *was* of makes the
 * reader go and look.
 */
const UNSAVEABLE_UNIT: Readonly<Record<'unused_template' | 'segment_never_sent', string>> = {
  unused_template: 'templates',
  segment_never_sent: 'segments',
};

/**
 * The name a saved cohort gets when the person did not type one.
 *
 * A **label**, not a sentence: the kind's chip word and the date the snapshot
 * was counted, e.g. `Dormant audience · 26 Sep 2026`. It is built from
 * `OPPORTUNITY_COPY[kind].label` so the segments list and the Overview tile
 * cannot come to call the same audience two things.
 *
 * The date is in it because a cohort is a snapshot and re-counting produces a
 * SECOND segment rather than refreshing this one — so without the date two
 * saves of the same audience a month apart would be two rows with one name,
 * and the older one is the one a scheduled campaign is pointed at.
 *
 * `en-GB` with a short month and **UTC**, matching `OPPORTUNITY_COPY.source`'s
 * register and the formatting in `report.ts`. UTC rather than the reader's zone
 * because this string is STORED: a name that rendered one date to the person
 * who saved it and another to a colleague in a different zone would be two
 * facts about one row.
 */
export function defaultCohortName(kind: SaveableOpportunityKind, countedAt: Date): string {
  const date = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(countedAt);
  return `${OPPORTUNITY_COPY[kind].label} · ${date}`;
}

/**
 * Every sentence a screen or an error says about saving a cohort.
 *
 * Here, and not in `apps/web`, and not in the API either. Half the defects this
 * project found in review were sentences, and a refusal written at the place it
 * is thrown is a sentence nobody reviews twice.
 *
 * None of these is an explanatory sentence ON a screen — the standing UI rule
 * holds, and a cohort shows a chip, a label, a date and a figure. These are
 * **refusals**: the words a person reads when something they asked for did not
 * happen, and each one names the repair.
 */
export interface CohortRefusals {
  /**
   * A POST asking to save a count of templates or of segments.
   *
   * Refused by {@link saveableOpportunityKindSchema} at the edge and by
   * `segments_cohort_kind_is_an_audience` at the database. The card only draws
   * the action on the three audience tiles, so this is the backstop — and a
   * backstop that says which of the two it caught.
   */
  readonly not_an_audience: (kind: 'unused_template' | 'segment_never_sent') => string;
  /**
   * **The cohort this save would hand back holds nobody.** Two ways in, and the
   * sentence is the same because the repair is: the count changed, and nothing
   * was saved.
   *
   * `opportunitySchema.count` is `.min(1)`, so the card never draws a tile over
   * nobody. What does happen is that the population emptied between the read and
   * the click — a campaign sent in another tab, an upload deleted, a suppression
   * added — **or** that an identical population is already saved as a cohort
   * whose people have since been deleted. The second is not a corner: the
   * fingerprint is a hash of ADDRESSES, so deleting an upload and re-uploading
   * the same file presents the same population to a cohort that a cascade has
   * emptied. `cohorts.service.ts` therefore tests the cohort's own
   * `uniqueRecipients` after the write rather than the rows this save inserted,
   * which is zero on the idempotent path by design.
   *
   * The whole transaction rolls back either way, so there is no named-but-empty
   * cohort left behind to explain.
   */
  readonly now_empty: string;
  /**
   * The same population is already saved, under a name the person did not ask
   * for.
   *
   * Refused rather than silently honoured: handing back a segment called
   * something other than what they typed is the screen lying about what they
   * just did. The count and the existing name are both in the sentence, because
   * a refusal a person cannot act on is a dead end.
   *
   * **`uniqueRecipients` is at least one, and there is deliberately no zero
   * arm.** *"These same 0 people are already saved as …"* is not a refusal
   * anybody can act on, and it cannot be produced: {@link now_empty} is checked
   * first, on both the inserted and the idempotent path, so an emptied cohort is
   * refused before a name is ever compared. The ordering is the guarantee; a
   * third sentence here would have been a second opinion about it.
   */
  readonly already_saved: (uniqueRecipients: number, existingName: string) => string;
  /** `PATCH /segments/:id` carrying `datasetIds` on a cohort. */
  readonly cohort_takes_no_datasets: string;
  /**
   * A campaign on a cohort whose every member has since been deleted.
   *
   * Deliberately NOT folded into `noSendableRecipientsMessage`'s existing "the
   * segment is empty", which stays correct for a static segment nobody has
   * filled yet. An empty static segment was never filled; an empty cohort was
   * filled and then emptied. Two different repairs, so two different sentences.
   */
  readonly cohort_now_empty_for_campaign: string;
}

export const COHORT_REFUSALS: CohortRefusals = {
  not_an_audience: (kind) =>
    `Only an audience can become a segment. “${OPPORTUNITY_COPY[kind].label}” is a count of ${UNSAVEABLE_UNIT[kind]}, not of people.`,
  now_empty:
    'There is nobody in this cohort any more — the count changed while this page was open. Nothing was saved.',
  already_saved: (uniqueRecipients, existingName) =>
    uniqueRecipients === 1
      ? `This same person is already saved as “${existingName}”. Rename that segment, or send to it.`
      : `These same ${uniqueRecipients.toLocaleString('en-IN')} people are already saved as “${existingName}”. Rename that segment, or send to it.`,
  cohort_takes_no_datasets:
    'A cohort holds the people it was saved with. It cannot be given datasets.',
  cohort_now_empty_for_campaign:
    'This cohort has nobody in it now: every contact it held has been removed from the workspace.',
};
